import { CustomFieldObject } from '~/models/customData';

import { isPlainObject } from '@vestigas/utils';

export type ChangesListPath = {
  /** Marks paths sourced from a custom field, so history UI can linkify URL values. */
  IS_CUSTOM_FIELD?: boolean;
  NAME: string;
  PATH: string[];
};

// Initializes custom data by validating fields and filtering based on visibility rules.
// Raw values are preserved - formatting happens in the UI layer (EditingHistory, getColumns, etc.)
export const initCustomData = async (
  customData: Record<string, any>,
  changesListIncludedPaths: ChangesListPath[] = [],
  customFieldsBulk: Map<string, Record<string, any>>,
) => {
  const processedCustomData = { ...customData };
  const updatedPaths = [...changesListIncludedPaths];

  for (const [customDataKey, customDataValue] of Object.entries(
    processedCustomData,
  )) {
    const customFieldId = CustomFieldObject.getIdFromKey(customDataKey);

    if (!customFieldId) {
      continue;
    }

    const customField = customFieldsBulk.get(customFieldId);

    if (!customField) {
      continue;
    }

    // Remove custom fields from the delivery note if the user isn't allowed to access them.
    // (e.g. when the custom fields are from another company).
    if (!customField) {
      delete processedCustomData[customDataKey];
      continue;
    }

    // CHANGES_LIST_INCLUDED_PATHS must be extended by the custom fields
    // level can throw "Cannot read properties of null (reading 'level')" error if customField has been deleted in the meantime.
    if (customField.level === CustomFieldObject.LEVEL.DELIVERY_NOTE.KEY) {
      updatedPaths.push({
        IS_CUSTOM_FIELD: true,
        NAME: customField.displayName,
        PATH: ['customData', customDataKey],
      });
    } else if (customField.level === CustomFieldObject.LEVEL.ARTICLE.KEY) {
      updatedPaths.push({
        IS_CUSTOM_FIELD: true,
        NAME: `Artikel - ${customField.displayName}`,
        PATH: ['customData', customDataKey],
      });
    }

    // Remove the key if the value is an object that can't be handled by the UI layer.
    // Some types are intentionally objects and have formatters in the UI layer:
    // - VALUE_MEASURE: { value, measure } → "1,5 kg"
    // - LEGAL_ORGANIZATION: { name, id, ... } → company name
    if (
      isPlainObject(customDataValue) &&
      customField.type !== CustomFieldObject.TYPE.VALUE_MEASURE.KEY &&
      customField.type !== CustomFieldObject.TYPE.LEGAL_ORGANIZATION.KEY
    ) {
      delete processedCustomData[customDataKey];
      continue;
    }

    // Remove the key if it shouldn't be displayed.
    if (
      [
        CustomFieldObject.VISIBILITY.NEVER.KEY,
        CustomFieldObject.VISIBILITY.WHEN_SET.KEY,
      ].includes(customField.visibility) &&
      CustomFieldObject.valueIsEmpty(customDataValue)
    ) {
      delete processedCustomData[customDataKey];
      continue;
    }
  }

  Object.assign(customData, processedCustomData);
  return updatedPaths;
};
